import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Link2 } from 'lucide-react';

interface SyncChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  sourceBoardName: string;
  onDuplicate: () => void;
  onSync: () => void;
}

export function SyncChoiceModal({
  isOpen,
  onClose,
  itemName,
  sourceBoardName,
  onDuplicate,
  onSync,
}: SyncChoiceModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add item to this board</DialogTitle>
          <DialogDescription>
            "{itemName}" is already in <strong>{sourceBoardName}</strong>. 
            How would you like to add it here?
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col gap-3 mt-4">
          {/* Duplicate option */}
          <button
            onClick={() => {
              onDuplicate();
              onClose();
            }}
            className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-left group"
          >
            <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
              <Copy className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-sm">Duplicate</h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                Create an independent copy. Changes won't affect the original.
              </p>
            </div>
          </button>
          
          {/* Sync option */}
          <button
            onClick={() => {
              onSync();
              onClose();
            }}
            className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-left group"
          >
            <div className="p-2 rounded-lg bg-muted group-hover:bg-partnr/10 transition-colors">
              <Link2 className="w-5 h-5 text-muted-foreground group-hover:text-partnr-plain" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-sm">Keep in sync</h4>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mirror the original. View here, edit in {sourceBoardName}.
              </p>
            </div>
          </button>
        </div>
        
        <div className="flex justify-end mt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
